var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import axiosBetterStacktrace from 'axios-better-stacktrace';
import { requestHeaderCacheControlNoStorage } from './utils';
/**
 * `ApiClientBase` is a encapsulation of the network request logic needed to interact with the backend
 * as an authenticated user. It's intended to be as stateless as possible to simplify testing (it
 * only receives a reference to an `AxiosInstance`, which we can mock out later to create a
 * `MockApiClientBase` for testing).
 */
class ApiClientBase {
    constructor({ baseUrl, vgsBaseUrl, userAgent }) {
        // Currently-registered refresh-on-401 interceptor, ejected before re-registering
        this.refreshInterceptorId = null;
        this.accessToken = '';
        this.vgsBaseUrl = vgsBaseUrl || '';
        this.client = axios.create({
            baseURL: baseUrl,
            timeout: 10000,
            headers: Object.assign(Object.assign({ 'Content-Type': 'application/json' }, (userAgent
                ? {
                    'User-Agent': userAgent,
                }
                : {})), requestHeaderCacheControlNoStorage),
        });
        axiosBetterStacktrace(this.client);
        this.client.interceptors.request.use((config) => {
            if (this.accessToken) {
                config.headers.set('authorization', `Bearer ${this.accessToken}`);
            }
            return config;
        });
    }
    /**
     * @param {string} t Token to inject into authorization header.
     */
    setAccessToken(t) {
        this.accessToken = t;
    }
    /**
     * `setRefreshAccessTokenMiddleware` adds a response interceptor that reacts to a 401 by calling
     * `refreshAccessToken` and storing the new token via `setAccessToken` for use by subsequent
     * requests. It does NOT resubmit the original failed request — that request's promise is always
     * rejected, so callers must retry (or rely on a higher-level retry, e.g. React Query) to see a
     * successful result after the refresh completes.
     * @param {() => Promise<string>} refreshAccessToken Function that returns a new access token.
     */
    setRefreshAccessTokenMiddleware(refreshAccessToken) {
        // Eject the previous handler so re-registration doesn't stack interceptors
        if (this.refreshInterceptorId !== null) {
            this.client.interceptors.response.eject(this.refreshInterceptorId);
        }
        this.refreshInterceptorId = this.client.interceptors.response.use(undefined, (error) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (error.config && ((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 401) {
                const accessToken = yield refreshAccessToken();
                this.setAccessToken(accessToken);
            }
            return Promise.reject(error);
        }));
    }
}
export { ApiClientBase };
